import React, { useState } from 'react';
import {
  AppShell,
  Group,
  Text,
  Button,
  ScrollArea,
  Badge,
  Box,
  useMantineTheme,
  Anchor,
} from '@mantine/core';
import {
  IconLogout,
  IconMenu2,
  IconMenuDeep,
  IconArchive,
  IconKey,
} from '@tabler/icons-react';
import { useNavigate, useLocation } from 'react-router-dom';
import DynamicMenu from '../components/DynamicMenu';
import ChangePasswordModal from '../components/ChangePasswordModal';
import ReleaseNotesModal from '../components/ReleaseNotesModal';
import AppMain from './AppMain';
import { APP_VERSION, MANTINE_VERSION, REACT_VERSION } from '../lib/appVersion';

export default function AppLayout({ user, onLogout, onUserUpdate }) {
  const [menuCollapsed, setMenuCollapsed] = useState(false);
  const [openMenus, setOpenMenus] = useState({});
  const [openSubMenus, setOpenSubMenus] = useState({});
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [releaseNotesOpen, setReleaseNotesOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useMantineTheme();
  const palette = theme.colors[theme.primaryColor] || theme.colors.grape;

  const displayName =
    user.prenom && user.nom ? `${user.prenom} ${user.nom}` : user.login;

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: menuCollapsed ? 60 : 250,
        breakpoint: 'sm',
      }}
      footer={{ height: 50 }}
      padding="md"
      styles={{
        root: { height: '100dvh', overflow: 'hidden' },
        main: {
          minHeight: 0,
          height: '100dvh',
          maxHeight: '100dvh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
        navbar: { transition: 'width 0.3s ease' },
      }}
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Button
              variant="subtle"
              size="sm"
              onClick={() => setMenuCollapsed((c) => !c)}
              title={menuCollapsed ? 'Agrandir le menu' : 'Réduire le menu'}
            >
              {menuCollapsed ? <IconMenuDeep size={18} /> : <IconMenu2 size={18} />}
            </Button>
            <Group gap="xs" wrap="nowrap">
              <IconArchive size={22} color={palette[6]} stroke={1.75} />
              <Text fw={700} size="sm" visibleFrom="xs" tt="uppercase" lts="0.04em">
                Archimage
              </Text>
            </Group>
          </Group>

          <Group gap="sm" wrap="nowrap">
            <Badge variant="light" color={theme.primaryColor} visibleFrom="sm">
              {user.role}
            </Badge>
            <Text
              size="sm"
              c="dimmed"
              visibleFrom="sm"
              style={{ cursor: 'pointer' }}
              onClick={() => navigate('/profil')}
              title="Mon profil"
            >
              {displayName}
            </Text>
            <Button
              variant="light"
              size="sm"
              leftSection={<IconKey size={16} />}
              onClick={() => setPasswordModalOpen(true)}
              title="Changer mon mot de passe"
            >
              <Box component="span" visibleFrom="sm">
                Mot de passe
              </Box>
            </Button>
            <Button
              variant="light"
              color="gray"
              size="sm"
              leftSection={<IconLogout size={16} />}
              onClick={onLogout}
              title="Quitter"
            >
              <Box component="span" visibleFrom="sm">
                Déconnexion
              </Box>
            </Button>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p={menuCollapsed ? 'xs' : 'md'}>
        {!menuCollapsed && (
          <ScrollArea flex={1} type="auto" offsetScrollbars>
            <DynamicMenu
              onSelectModule={(path) => navigate(path)}
              currentPath={location.pathname}
              openMenus={openMenus}
              setOpenMenus={setOpenMenus}
              openSubMenus={openSubMenus}
              setOpenSubMenus={setOpenSubMenus}
            />
          </ScrollArea>
        )}
      </AppShell.Navbar>

      <AppShell.Main>
        <Box
          style={{
            flex: 1,
            minHeight: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <AppMain user={user} onUserUpdate={onUserUpdate} />
        </Box>
      </AppShell.Main>

      <AppShell.Footer>
        <Group h="100%" px="md" justify="center">
          <Text size="xs" c="dimmed">
            © {new Date().getFullYear()} Archimage — KODALA.dev ·{' '}
            <Anchor
              component="button"
              type="button"
              size="xs"
              color={theme.primaryColor}
              onClick={() => setReleaseNotesOpen(true)}
            >
              v{APP_VERSION} — Release notes
            </Anchor>{' '}
            · React {REACT_VERSION} & Mantine {MANTINE_VERSION}
          </Text>
        </Group>
      </AppShell.Footer>

      <ChangePasswordModal
        opened={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
        onChanged={onLogout}
      />
      <ReleaseNotesModal
        opened={releaseNotesOpen}
        onClose={() => setReleaseNotesOpen(false)}
        appVersion={APP_VERSION}
      />
    </AppShell>
  );
}
